// Canlı özet: KDV dahil/hariç fiyat, kâr marjı ve eksik zorunlu alanlar.
import React from 'react';
import { Box, Chip, Divider, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import type { StockCardForm } from '../useStockCardForm';

const money = (v: number, code: string) =>
  `${v.toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ${code === 'TRY' ? '₺' : code}`;

const Row: React.FC<{ label: string; value: string; strong?: boolean }> = ({ label, value, strong }) => (
  <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 1, fontVariantNumeric: 'tabular-nums' }}>
    <Typography variant="body2" color="text.secondary">{label}</Typography>
    <Typography variant="body2" fontWeight={strong ? 700 : 500}>{value}</Typography>
  </Box>
);

export interface SummaryProblem { section: string; message: string }

export const SummaryPanel: React.FC<{ f: StockCardForm; problems: SummaryProblem[]; onGoTo: (section: string) => void }> = ({
  f, problems, onGoTo,
}) => {
  const { t } = useTranslation('accounting');
  const vat = typeof f.vatRate === 'number' ? f.vatRate : null;
  const sale = typeof f.salePrice === 'number' && f.salePrice > 0 ? f.salePrice : null;
  const purchase = typeof f.purchasePrice === 'number' && f.purchasePrice > 0 ? f.purchasePrice : null;
  const included = f.vatStatus === 'included';
  const ready = vat !== null && f.vatStatus !== '';

  const toExcl = (v: number) => (included ? v / (1 + (vat ?? 0) / 100) : v);
  const toIncl = (v: number) => (included ? v : v * (1 + (vat ?? 0) / 100));

  const saleExcl = sale !== null && ready ? toExcl(sale) : null;
  const saleIncl = sale !== null && ready ? toIncl(sale) : null;
  const sameCurrency = f.purchaseCurrency === f.saleCurrency;
  const purchaseExcl = purchase !== null && ready ? toExcl(purchase) : null;
  const margin = saleExcl && purchaseExcl !== null && sameCurrency && saleExcl > 0
    ? ((saleExcl - purchaseExcl) / saleExcl) * 100
    : null;

  return (
    <Box sx={{ display: 'grid', gap: 1.25 }}>
      <Typography variant="overline" color="text.secondary" sx={{ lineHeight: 1.4 }}>
        {t('stock.addModal.layout.summary', 'Canlı özet')}
      </Typography>
      {sale === null || !ready ? (
        <Typography variant="body2" color="text.secondary">
          {t('stock.addModal.layout.summaryEmpty', 'KDV ve satış fiyatı girildiğinde KDV dahil/hariç fiyat ve kâr marjı burada görünür.')}
        </Typography>
      ) : (
        <>
          <Row label={t('stock.addModal.layout.saleIncl', 'Satış (KDV dahil)')} value={money(saleIncl!, f.saleCurrency)} strong />
          <Row label={t('stock.addModal.layout.saleExcl', 'Satış (KDV hariç)')} value={money(saleExcl!, f.saleCurrency)} />
          {purchaseExcl !== null && (
            <Row label={t('stock.addModal.layout.purchaseExcl', 'Alış (KDV hariç)')} value={money(purchaseExcl, f.purchaseCurrency)} />
          )}
          {margin !== null && (
            <Row
              label={t('stock.addModal.layout.margin', 'Kâr marjı')}
              value={`%${margin.toLocaleString('tr-TR', { maximumFractionDigits: 1 })}`}
              strong
            />
          )}
          {purchase !== null && !sameCurrency && (
            <Typography variant="caption" color="text.secondary">
              {t('stock.addModal.layout.marginCurrency', 'Alış ve satış farklı para biriminde; marj hesaplanmadı.')}
            </Typography>
          )}
        </>
      )}

      <Divider />
      <Typography variant="overline" color="text.secondary" sx={{ lineHeight: 1.4 }}>
        {t('stock.addModal.layout.cardStatus', 'Kart durumu')}
      </Typography>
      {problems.length === 0 ? (
        <Chip size="small" color="success" variant="outlined" label={t('stock.addModal.layout.readyToSave', 'Kaydetmeye hazır')} sx={{ justifySelf: 'start' }} />
      ) : (
        problems.map(p => (
          <Chip
            key={p.message}
            size="small"
            color="warning"
            variant="outlined"
            label={p.message}
            onClick={() => onGoTo(p.section)}
            sx={{ justifySelf: 'start', maxWidth: '100%', height: 'auto', '& .MuiChip-label': { whiteSpace: 'normal', py: 0.5 } }}
          />
        ))
      )}
      {f.productionEnabled && f.itemType === 'PRODUCT' && (
        <Row
          label={t('stock.addModal.layout.saleDeduction', 'Satışta düşüm')}
          value={f.productionMode === 'MAKE_TO_STOCK'
            ? t('stock.addModal.layout.deductSelf', 'Ürünün kendisi')
            : t('stock.addModal.layout.deductRecipe', 'Reçete varsa malzemeleri')}
        />
      )}
    </Box>
  );
};
